<script setup lang="ts">
import "./dockview.css";

import { computed, ref } from "vue";
import { DockviewVue, themeReplit } from "dockview-vue";

import { useBrandStore } from "@/stores/brand-store";
import { useUserSessionStore } from "@/stores/user-session-store";

import DockviewWrapper from "@/components/Dockview/DockviewWrapper.vue";

import type { Component } from "vue";
import type { PanelMetadata, PanelProps, PanelType } from "@/hooks/use-case-panel";
import type { DockviewApi, DockviewReadyEvent, DockviewTheme, IDockviewPanel, SerializedDockview } from "dockview-vue";

withDefaults(defineProps<{ allPanels: PanelMetadata }>(), {  })

defineOptions({ components: { panelWrapper: DockviewWrapper as Component } });
defineExpose({ openPanel });

const dockviewAPI = ref<DockviewApi>();
const dockviewTheme = ref<DockviewTheme>({ ...themeReplit, gap: 0 });

const dockviewHasInitialized = ref(false);
const dockviewHasToggledClosedPanels = ref(false);

const selectedLens = computed(() => useBrandStore().selectedLens.code);

const closedPanels = ref(useUserSessionStore().brandLayout?.closedPanelLastGroup?.[selectedLens.value] ?? {});

const allClosed = computed(() => {
  for (const panelId in __props.allPanels) {
    const panel = __props.allPanels[panelId as PanelType];
    if (panel.enabled === true && !(panelId in closedPanels.value)) return false;
  }

  return true;
});

async function onReady(event: DockviewReadyEvent) {
  const { brandLayout } = useUserSessionStore();

  dockviewAPI.value = event.api;
  event.api.onDidLayoutChange(() => {
    // When the closed panels are initially opened, the delay after adding them would
    // give enough time for this event to be triggered. Then after they're closed, the flag is
    // flipped. Allowing the next layoutChange event forced from the accumulated close
    //  to be classed as the full initialization event.
    if (!dockviewHasToggledClosedPanels.value) return;

    // This event is triggered after all panels have been added, including the closed panels to be initially
    // toggled for initialization. This should always be treated as the full initialization marker, and all
    // conditions before it should enforce it in such a manner, to prevent user-triggered layout changes from
    // being discarded.
    if (!dockviewHasInitialized.value) {
      dockviewHasInitialized.value = true;
      return;
    }

    saveLayout();
  });
  event.api.onDidRemovePanel((closedPanel) => {
    if (!dockviewHasToggledClosedPanels.value) return;
    updateClosedPanels(closedPanel);
  });
  event.api.onDidAddPanel((panel) => {
    const panelParams = panel.params as PanelProps<boolean>;

    // Either illegally added, or the panel is deprecated and removed from the allowed panel list
    if (!(panelParams.panelType in __props.allPanels)) {
      try {
        panel.api.close(); // Close instantly
      } catch (error) {
        console.warn(`Error closing invalid panel - "${panelParams.panelType}"`);
      }
      return;
    }

    if (!dockviewHasToggledClosedPanels.value) return;

    const { enabled = false, popoutEnabled = false } = __props.allPanels[panelParams.panelType] ?? {};

    // This added panel is a popout and will eventually be moved to a popout window
    if (panelParams.popoutRouteParams != null) {
      // Close if the panel doesn't support/allow popouts
      if (!popoutEnabled) panel.api.close();
      return;
    }

    if (enabled) updateClosedPanels();
    else panel.api.close(); // Close panel since it's not enabled
  });

  if (brandLayout?.provider === "dockview") {
    try {
      const lensLayout = brandLayout.lenses[selectedLens.value];
      if (lensLayout == null) throw new Error("Lens has no saved session");

      const state = JSON.parse(lensLayout) as SerializedDockview;

      if (Object.keys(state.panels).length < 1) throw new Error("No Panels in Saved Session");
      else event.api.fromJSON(state);
    } catch (error) {
      loadDefaultLayout(event);
    }
  } else loadDefaultLayout(event);

  const addedClosedPanelsToClose = Object.entries(__props.allPanels).flatMap((arg) => {
    const panelId = arg[0] as PanelType;
    const panel = event.api.getPanel(panelId);

    // Panel to be newly created
    if (panel == null) return addPanel(panelId, { inactive: true, forcedForInitialMount: true });
    // Panel already exists probably from restored layout, but should be closed as it is disabled
    else if (__props.allPanels[panelId].enabled !== true) return panel;

    return [];
  });

  if (addedClosedPanelsToClose.length > 0) {
    await new Promise((res) => setTimeout(res));
    addedClosedPanelsToClose.forEach((panel) => {
      try {
        panel?.api.close();
      } catch (e) {
        console.error(e);
      }
    });
  }

  dockviewHasToggledClosedPanels.value = true;

  updateClosedPanels();

  const closedAfterUpdate = Object.keys(closedPanels.value);
  const closedInSession = Object.keys(brandLayout?.closedPanelLastGroup?.[selectedLens.value] ?? {});

  if (
    closedInSession.length !== closedAfterUpdate.length ||
    // Check to confirm all the panels that have been closed are updated in the user session
    closedAfterUpdate.some((panelId) => !closedInSession.includes(panelId))
  ) {
    saveLayout();
  }
}

function loadDefaultLayout(event: DockviewReadyEvent) {
  const lensLayout = useBrandStore().viewerConfig.flexDefaultLayout;

  if (lensLayout?.provider === "dockview") {
    try {
      const state = JSON.parse(lensLayout.state) as SerializedDockview;
      if (Object.keys(state.panels).length > 0) event.api.fromJSON(state);
      return;
    } catch (error) {}
  }

  Object.entries(__props.allPanels)
    .filter(([, a]) => a.defaultOpen !== false)
    // sort panels with undefined orders to the end
    .sort(([, a], [, b]) => (a.order == null || b.order == null ? a.title.localeCompare(b.title) : a.order - b.order))
    .forEach(([id]) => addPanel(id as PanelType));
}

function saveLayout() {
  if (dockviewAPI.value == null) return;

  const state = dockviewAPI.value.toJSON();

  // Don't save if there's any active popout groups
  if (state.popoutGroups?.some((pg) => pg.data.activeView != null) ?? false) return;
  state.popoutGroups = undefined;

  // A panel was created that isn't one of the top-level panels
  if (Object.keys(state.panels).find((panelId) => !(panelId in __props.allPanels)) != null) return;

  const { brandSort: sort, brandLayout: layout } = useUserSessionStore();

  void useUserSessionStore().saveBrandSession({
    sort,
    layout: {
      provider: "dockview",
      lenses: { ...layout?.lenses, [selectedLens.value]: JSON.stringify(state) },
      closedPanelLastGroup: { ...layout?.closedPanelLastGroup, [selectedLens.value]: closedPanels.value },
    },
  });
}

function updateClosedPanels(closedPanel?: IDockviewPanel) {
  if (dockviewAPI.value == null) return;

  const openPanels = dockviewAPI.value.panels.map((panel) => (panel.params as PanelProps<boolean>).panelType);

  closedPanels.value = Object.fromEntries(
    Object.entries(__props.allPanels).flatMap(([id, panel]) => {
      if (openPanels.includes(id as PanelType) || panel.enabled === false) return [];
      return [[id, closedPanel?.id === id ? closedPanel.group.id : (closedPanels.value?.[id as PanelType] ?? null)]];
    }),
  );
}

function openPanel(panelType: PanelType) {
  addPanel(panelType, { referenceGroupId: closedPanels.value[panelType] ?? undefined }); // Add panel
  updateClosedPanels(); // Update closed panels
}

function addPanel(
  panelType: PanelType,
  {
    inactive = false,
    forcedForInitialMount = false,
    referenceGroupId,
  }: { inactive?: boolean; forcedForInitialMount?: boolean; referenceGroupId?: string } = {},
) {
  if (dockviewAPI.value == null || __props.allPanels[panelType] == null) return;
  const { title, enabled = true } = __props.allPanels[panelType];

  // If forcing the panel's add for the initial mount, verify the flag has not been toggled
  // to indicate the process hasn't already been completed. Otherwise, ensure the panel is enabled
  if (forcedForInitialMount ? dockviewHasToggledClosedPanels.value : !enabled) return;

  const referenceGroup =
    referenceGroupId == null ? undefined : dockviewAPI.value.groups.find((g) => g.id === referenceGroupId);

  return dockviewAPI.value.addPanel({
    id: panelType,
    title,
    component: "panelWrapper",
    renderer: "always",
    params: { panelType } satisfies PanelProps<false>,
    inactive,
    position: referenceGroup == null ? undefined : { referenceGroup },
  });
}
</script>
<template>
  <DockviewVue
    scrollbars="native"
    class="tw-size-full"
    default-renderer="always"
    disable-tabs-overflow-list
    :theme="dockviewTheme"
    @ready="onReady"
  />
  <div
    v-if="allClosed"
    class="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-gray-100"
  >
    <p class="tw-text-pretty tw-text-gray-700">All panels are closed.</p>
  </div>
</template>
